import { fetchAuthSession } from '@aws-amplify/auth';

import { BACKEND_AGENTS_API } from '../env';

// Pantalla "Auditoría → Consultas". Mismo API Gateway que los agentes
// (agents-admin, ver src/agents-admin/audit_admin.py). El backend acota
// solas por defecto a las invocaciones propias del usuario -- solo un
// admin (permiso agents.manage) puede ver/filtrar por todo el tenant, ese
// control vive del lado del servidor, no acá.
const API_BASE = BACKEND_AGENTS_API;
const AUDIT_URL = `${API_BASE}/audit/invocations`;
const AUDIT_SUMMARY_URL = `${API_BASE}/audit/summary`;

async function getAuthHeader() {
  try {
    const idToken = (await fetchAuthSession()).tokens?.idToken?.toString();
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
  } catch (error) {
    console.error('AuditService.getAuthHeader: unable to fetch session', error);
    return {};
  }
}

async function request(path, init = {}) {
  const authHeader = await getAuthHeader();
  const headers = {
    Accept: 'application/json',
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    ...authHeader
  };
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  let response;
  try {
    response = await fetch(url, { ...init, headers, mode: 'cors', cache: 'no-cache' });
  } catch (networkError) {
    throw new Error(`No se pudo contactar el servicio de auditoría. ${networkError.message}`);
  }

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    let message = raw || `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(raw);
      message = parsed.error || parsed.message || message;
    } catch {
      /* texto plano */
    }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }

  if (response.status === 204) {
    return null;
  }
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const AuditService = {
  // { question, userId, model, limit, cursor } -- todos opcionales. `userId`
  // solo tiene efecto si el caller es admin (agents.manage); si no lo es, el
  // backend lo ignora y acota igual a sus propias invocaciones.
  async listInvocations({ question, userId, model, limit, cursor } = {}) {
    const qs = new URLSearchParams();
    if (question) qs.set('question', question);
    if (userId) qs.set('user_id', userId);
    if (model) qs.set('model', model);
    if (limit) qs.set('limit', String(limit));
    if (cursor) qs.set('cursor', cursor);
    const query = qs.toString();
    const data = await request(query ? `${AUDIT_URL}?${query}` : AUDIT_URL, { method: 'GET' });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      nextCursor: data?.next_cursor || null,
      canViewAll: Boolean(data?.can_view_all)
    };
  },

  // Costo promedio por pregunta de cada modo del chat (basic, deep,
  // dashboard) en los últimos 30 días, con los mismos filtros y alcance que
  // listInvocations: { periodDays, modes: { basic: { count, avg_credits }, ... } }.
  async getSummary({ question, userId, model } = {}) {
    const qs = new URLSearchParams();
    if (question) qs.set('question', question);
    if (userId) qs.set('user_id', userId);
    if (model) qs.set('model', model);
    const query = qs.toString();
    const data = await request(query ? `${AUDIT_SUMMARY_URL}?${query}` : AUDIT_SUMMARY_URL, { method: 'GET' });
    return {
      periodDays: data?.period_days || 30,
      modes: data?.modes || {}
    };
  }
};

export default AuditService;
